import {
  booleanLiteralPattern,
  dqlUnitSuffixPattern,
  nullLiteralPattern,
  parameterNamePattern,
  sortedDqlCommands,
} from '../../core/dql/dql-common.js';

// All characters except for newline, carriage return, form feed, vertical tab characters
const singleLineCommentPattern = /\/\/[^\n\r\v\f]*/;
// Matches `/*` followed by any characters until the next `*/`.
const multiLineCommentPattern = /\/\*[\s\S]*?\*\//m;

const comments = {
  comment: {
    pattern: singleLineCommentPattern,
    greedy: true,
  },
  commentMultiline: {
    pattern: multiLineCommentPattern,
    alias: 'comment',
    greedy: true,
  },
};

/**
 * Matches double-quoted strings allowing escaped quotes.
 *
 * This regular expression matches strings enclosed in double quotes,
 * where the quotes can be escaped using a backslash.
 * Note that if a backslash is itself escaped (`\\`), it *doesn't* escape the quote that follows.
 *
 * @example
 * // ✔ Matches:
 * // "string"
 * // "string\"with\"quotes"
 * // "string\\"
 * // "string\\\"with\\\"quotes"
 * // "string\\\\"
 *
 * // ✘ Does not match:
 * // "string (missing closing quote)
 * // "string"with"quotes" (only matches "string")
 * // "string\" (missing closing quote)
 * // "string\\"with\\"quotes" (only matches "string\\")
 * // "string\\\" (missing closing quote)
 * // etc.
 *
 * Please be careful when modifying this RegEx. Minor changes may cause exponential runtime complexity:
 * const badPattern = /"(?:[^"\\]+|\\.)*"/m
 *
 * @see Friedl, J. E. (2006). Mastering regular expressions. O'Reilly Media, Inc., pages 196-198, 223-228
 */
const stringPattern = /"(?:[^"\\]|\\.)*"/m;

/**
 * @see stringPattern
 */
const backtickStringPattern = /`(?:[^`\\]|\\.)*`/m;

/**
 * Matches a triple-quoted string by enforcing that the content cannot contain
 * 3+ consecutive quotes. The structure is:
 *   - Opening """ (3 quotes)
 *   - Up to 2 additional leading quotes (part of content)
 *   - Any number of (non-quote char + up to 2 quotes) groups
 *   - Closing """ (3 quotes)
 *
 * This handles tricky cases on a single line:
 *   - Multiple adjacent triple-quoted strings: """netcoreclient""" AND """sdv1"""
 *     Each string is matched independently, the tokens between them are not swallowed.
 *   - Content that starts or ends with extra quotes: """"prefix""" or """suffix""""
 *     The bounded "?"? quantifiers ensure the closing """ is found correctly.
 */
const tripleQuoteStringPattern = /""""?"?(?:[^"]"?"?)*"""/m;

/**
 * Matches only known DQL command names, used for keyword highlighting
 */
const dqlCommandNamePattern = new RegExp(
  `\\b(?:${sortedDqlCommands.join('|')})\\b`,
  'i',
);

/**
 * Matches any alphabetic sequence. Used for structural regex composition in
 * commandParametersPattern and commandPattern, where backtracking splits the
 * command name from the parameters. Not used for keyword highlighting.
 */
const commandNamePattern = /[A-Za-z]+/;

/**
 * Matches any string as long as it doesn't contain the character | (this is a command separator in dql),
 * unless that | is contained inside a double-quoted, triple-quoted, or backtick string.
 * Triple-quoted strings are matched first so that their content (which may include lone " characters)
 * is consumed atomically and doesn't leave unmatched quotes that would cause the double-quote
 * pattern to run away across | boundaries and break subsequent command highlighting.
 *
 * Given a hypothetical dql:
 * FETCH metric = "string" | AGGREGATE X
 * This regex would match:
 * metric = "string"
 * X
 * As well as other possibilities:
 * metric = "string containing |"
 * metric = "string containing multiple |||" and secondMetric = Y
 * metric = """triple-quoted string containing |"""
 * metric = `backtick string containing |`
 * metric = parameter
 * metric = "value" // comment containing | pipe
 */
const commandParametersPattern = new RegExp(
  `(${commandNamePattern.source})(?:${tripleQuoteStringPattern.source}|${backtickStringPattern.source}|${stringPattern.source}|${singleLineCommentPattern.source}|${multiLineCommentPattern.source}|[^|])*`,
);

/**
 * A command (FILTER parameter = "string" |) is made up of three parts:
 * command name made up of one word (FETCH)
 * command parameters (parameter = "string")
 * an optional command separator (|)
 */
const commandPattern = new RegExp(
  `${commandNamePattern.source}${commandParametersPattern.source}\\|?`,
  'm',
);

const commandName = {
  commandName: {
    pattern: dqlCommandNamePattern,
    alias: 'keyword',
  },
};

/**
 * @public
 */
export const prismDqlLanguage = {
  ...comments,
  /**
   * DQL language is defined as commands separated by the optional character (|).
   * Prism first matches an entire command, then matches the command parameters and command name
   * 'inside' the command.
   * Within the command parameters, Prism matches strings, variables, and numbers.
   *
   * Take great care of adding or modifying the regex patterns as this
   * may introduce recursion issues, due to the existence of three levels of regex matching.
   */
  command: {
    pattern: commandPattern,
    greedy: true,
    inside: {
      commandParameters: {
        pattern: commandParametersPattern,
        lookbehind: true,
        inside: {
          tripleQuoteString: {
            pattern: tripleQuoteStringPattern,
            alias: 'string',
          },
          string: {
            pattern: stringPattern, // Support strings surrounded by double quotes and include possible escaped quotes within
          },
          backtickstring: {
            pattern: backtickStringPattern,
            alias: 'string',
          },
          number: new RegExp(`\\b\\d+(?:\\.\\d+)?${dqlUnitSuffixPattern}?\\b`), // Match numbers with optional unit suffix
          boolean: booleanLiteralPattern, // Match boolean literals (true/false)
          null: { pattern: nullLiteralPattern, alias: 'boolean' }, // null literal, styled as boolean
          parameterName: { pattern: parameterNamePattern, alias: 'variable' },
          ...comments,
          commandName: {
            pattern: new RegExp(
              `(\\[\\s*)${dqlCommandNamePattern.source}`,
              'i',
            ),
            lookbehind: true,
            alias: 'keyword',
          },
        },
        ...commandName,
      },
      ...commandName,
    },
  },
};
